/**
 * Step 7: Later Life & Estate
 * Per-person deceased age, survivor spending, and social care cost modelling
 */

import { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StepTwilightYearsFormData, StepTwilightYearsSchema, CareScenarioItem, parseLocalDate, type AllStepFormData } from '../../types/wizard';
import { formatYearAge } from '../../utils/yearAgeLabel';
import { planYearToCalendar } from '../../utils/formatters';
import { useCurrencyInput } from '../../hooks/useCurrencyInput';
import { isFeatureEnabled } from '../../config/features';
import { wizardFormKeyDown } from './wizardFormKeyDown';
import { TourButton } from '../TourGuide';
import { WIZARD_STEP7_TOUR } from '../TourGuide/tourSteps';
import './StepTwilightYears.css';

const CARE_TYPE_PRESETS: Record<CareScenarioItem['careType'], { label: string; defaultCost: number; description: string; typicalDuration: string }> = {
  HOME: { label: 'Home Care', defaultCost: 25000, description: 'Daily visits, home adaptations', typicalDuration: '2-4 years' },
  RESIDENTIAL: { label: 'Residential Care', defaultCost: 50000, description: 'Care home (no nursing), typically ongoing until death', typicalDuration: '2-3 years' },
  NURSING: { label: 'Nursing Home', defaultCost: 75000, description: 'Nursing home with medical care, typically ongoing until death', typicalDuration: '1-2 years' },
};

interface PersonInfo {
  fullName: string;
  dateOfBirth?: string;
  retirementDate?: string;
}

/** Compute a person's age at plan start (year 1). In joint plans, plan start may differ from their retirement. */
function getAgeAtPlanStart(person: PersonInfo, planAsOfDate?: string): number | null {
  if (!person.dateOfBirth || !planAsOfDate) return null;
  const dob = parseLocalDate(person.dateOfBirth);
  const planStart = parseLocalDate(planAsOfDate);
  if (!dob || !planStart) return null;
  let age = planStart.getFullYear() - dob.getFullYear();
  if (planStart.getMonth() < dob.getMonth() || (planStart.getMonth() === dob.getMonth() && planStart.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

interface StepTwilightYearsProps {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: StepTwilightYearsFormData) => void;
  onSave: (data: StepTwilightYearsFormData) => void;
  onBack?: () => void;
  onGoToStep?: (step: number) => void;
  lastSaved?: string;
}

function StepTwilightYears({ initialData, onNext, onSave, onBack, onGoToStep, lastSaved }: Readonly<StepTwilightYearsProps>) {
  const persons: PersonInfo[] = initialData?.persons || [];
  const isJointPlan = initialData?.planType === 'JOINT';
  const personCount = isJointPlan ? 2 : 1;

  // Pre-compute each person's age at plan start (year 1) for age hints and duration calculations
  const ageAtPlanStartByPerson = persons.map(p => getAgeAtPlanStart(p, initialData?.planAsOfDate));

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<StepTwilightYearsFormData>({
    resolver: zodResolver(StepTwilightYearsSchema),
    defaultValues: {
      hasDirectDescendants: initialData?.hasDirectDescendants ?? null,
      includePensionsInEstate: initialData?.includePensionsInEstate ?? true,
      careScenarios: initialData?.careScenarios || [],
      personDeceasedAges: initialData?.personDeceasedAges ||
        Array.from({ length: personCount }, (_, i) => ({ personIndex: i, deceasedAge: null })),
      survivorSpendingPct: initialData?.survivorSpendingPct ?? 70,
      ihtStrategies: initialData?.ihtStrategies ?? {
        annualGiftExemption: false,
        smallGiftRecipientsPerYear: 0,
        charitableLegacyEnabled: false,
        normalExpenditureFromIncome: false,
        annualPetAmount: 0,
        annualCharitableDonation: 0,
      },
      // Ring-fenced care fund: omission, never null (server schema rejects null)
      careFund: initialData?.careFund ?? undefined,
      careFundReleaseAge: initialData?.careFundReleaseAge ?? undefined,
    },
  });

  const careFundInput = useCurrencyInput({
    value: watch('careFund'),
    registerProps: register('careFund', { valueAsNumber: true }),
  });

  const {
    fields: careFields,
    append: appendCare,
    remove: removeCare,
  } = useFieldArray({
    control,
    name: 'careScenarios',
  });

  const [careOpen, setCareOpen] = useState(() => (initialData?.careScenarios || []).length > 0);

  // Derive primary residence info for care-triggered sale option
  // Check both multi-property array and legacy single-property format
  const propertiesArray = initialData?.properties || [];
  const primaryResidence = propertiesArray.find(
    (p: { propertyType: string }) => p.propertyType === 'PRIMARY_RESIDENCE'
  );
  // Legacy: single property is always PRIMARY_RESIDENCE
  const legacyProperty = !primaryResidence && initialData?.includeProperty && initialData?.property
    ? initialData.property
    : null;
  const hasPrimaryResidence = !!primaryResidence || !!legacyProperty;
  const primaryResidenceLabel = primaryResidence?.label || legacyProperty?.label || 'primary residence';
  // Check both planningSale (multi-property format) and plannedSaleDate (legacy) for downsizing detection
  const hasSalePlanned = primaryResidence?.planningSale || primaryResidence?.plannedSaleDate;
  const isDownsizing = hasSalePlanned && (primaryResidence?.newPropertyPurchasePrice ?? 0) > 0;

  const handleBlur = () => {
    const data = watch();
    onSave(data);
  };

  const onSubmit = (data: StepTwilightYearsFormData) => {
    onNext(data);
  };

  /** Compute the duration that fills from startYear to end of life (or projection end). */
  const computeEndOfLifeDuration = (personIdx: number, startYear: number): number | null => {
    const deceasedAge = watch(`personDeceasedAges.${personIdx}.deceasedAge`);
    const ageAtPlanStart = ageAtPlanStartByPerson[personIdx];
    if (ageAtPlanStart != null && deceasedAge != null && deceasedAge > 0) {
      const startAge = ageAtPlanStart + startYear - 1;
      return Math.max(1, deceasedAge - startAge + 1);
    }
    // Fall back to projection years
    const projYears = initialData?.projectionYears;
    if (projYears) return Math.max(1, projYears - startYear + 1);
    return null;
  };

  const addCareScenario = () => {
    appendCare({
      scenarioId: crypto.randomUUID(),
      personIndex: 0,
      careType: 'HOME',
      annualCost: CARE_TYPE_PRESETS.HOME.defaultCost,
      startYear: 15,
      durationYears: 3,
      untilEndOfLife: false,
      inflationPremiumPct: 2,
      sellPrimaryResidenceOnCareStart: false,
      careSaleProceedsStrategy: 'CASH' as const,
    });
    setCareOpen(true);
  };

  const handleCareTypeChange = (index: number, newType: CareScenarioItem['careType']) => {
    setValue(`careScenarios.${index}.careType`, newType);
    setValue(`careScenarios.${index}.annualCost`, CARE_TYPE_PRESETS[newType].defaultCost);
    setTimeout(handleBlur, 0);
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <div className="step-header-row">
          <h2>Later Life & Estate</h2>
          <TourButton tour={WIZARD_STEP7_TOUR} variant="text" />
        </div>
        <p>
          Plan for later-life considerations: expected lifespan, survivor spending adjustments,
          and potential social care costs.
        </p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} onKeyDown={wizardFormKeyDown}>
        {/* Direct Descendants Section */}
        <div className="twilight-section" data-tour="descendants">
          <h3 className="twilight-section-header">Direct Descendants</h3>
          <p className="help-text assumptions-note">
            This affects your inheritance tax calculation.
          </p>
          <details className="rnrb-disclosure">
            <summary>Why does this matter?</summary>
            <p>
              Leaving your main residence to lineal descendants (children, grandchildren,
              great-grandchildren, etc.) qualifies your estate for the Residence Nil-Rate Band
              (RNRB) — an additional £175,000 inheritance tax allowance per person (2024/25, frozen
              until April 2028). The RNRB tapers by £1 for every £2 your estate exceeds £2 million,
              and is fully lost above £2.35M (single) or £2.7M (couple).
            </p>
          </details>
          <fieldset className="form-group">
            <legend className="form-label">
              Do you have lineal descendants (children, grandchildren, etc.)?
            </legend>
            <div className="radio-group">
              {/* Manual setValue/watch instead of register() because the field is boolean|null, not a string enum */}
              <label className="radio-option">
                <input
                  type="radio"
                  name="hasDirectDescendants"
                  value="yes"
                  aria-label="Yes — has lineal descendants"
                  checked={watch('hasDirectDescendants') === true}
                  onChange={() => {
                    setValue('hasDirectDescendants', true, { shouldDirty: true, shouldValidate: true });
                    handleBlur();
                  }}
                />
                <span className="radio-label">
                  <strong>Yes</strong>
                </span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="hasDirectDescendants"
                  value="no"
                  aria-label="No — no lineal descendants"
                  checked={watch('hasDirectDescendants') === false}
                  onChange={() => {
                    setValue('hasDirectDescendants', false, { shouldDirty: true, shouldValidate: true });
                    handleBlur();
                  }}
                />
                <span className="radio-label">
                  <strong>No</strong>
                </span>
              </label>
            </div>
            {errors.hasDirectDescendants && (
              <span className="error-message">{errors.hasDirectDescendants.message}</span>
            )}
          </fieldset>
        </div>

        {/* Pensions & Inheritance Tax Section */}
        <div className="twilight-section">
          <h3 className="twilight-section-header">Pensions &amp; Inheritance Tax</h3>
          <p className="help-text assumptions-note assumptions-note--amber">
            The UK Government announced in Autumn Budget 2024 that pension funds (SIPPs) will
            be included in estates for Inheritance Tax purposes from April 2027. This has been
            announced but not yet legislated.
          </p>
          <fieldset className="form-group">
            <legend className="form-label">
              Include pensions (SIPPs) in IHT estate calculation?
            </legend>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="includePensionsInEstate"
                  value="yes"
                  aria-label="Yes — model post-April 2027 proposed rules"
                  checked={watch('includePensionsInEstate') === true}
                  onChange={() => {
                    setValue('includePensionsInEstate', true, { shouldDirty: true, shouldValidate: true });
                    handleBlur();
                  }}
                />
                <span className="radio-label">
                  <strong>Yes</strong> — model post-April 2027 proposed rules
                </span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="includePensionsInEstate"
                  value="no"
                  aria-label="No — use current rules (pensions outside estate)"
                  checked={watch('includePensionsInEstate') === false}
                  onChange={() => {
                    setValue('includePensionsInEstate', false, { shouldDirty: true, shouldValidate: true });
                    handleBlur();
                  }}
                />
                <span className="radio-label">
                  <strong>No</strong> — use current rules (pensions outside estate)
                </span>
              </label>
            </div>
          </fieldset>
        </div>

        {/* IHT Reduction Strategies Section — behind disclosure */}
        {(watch('hasDirectDescendants') !== null || watch('includePensionsInEstate')) && (
          <details className="twilight-section iht-disclosure">
            <summary className="twilight-section-header iht-disclosure-summary">
              I want to model inheritance tax reduction strategies
            </summary>
            <p className="help-text assumptions-note">
              Model common IHT planning techniques. These reduce your estate (and portfolio)
              over time. Gifts are real cash outflows deducted from your liquid assets each year.
            </p>

            <div className="iht-strategy-grid">
              {/* Annual Gift Exemption */}
              <label className="iht-strategy-card">
                <div className="iht-strategy-toggle">
                  <input
                    type="checkbox"
                    {...register('ihtStrategies.annualGiftExemption')}
                    onChange={(e) => {
                      void register('ihtStrategies.annualGiftExemption').onChange(e);
                      handleBlur();
                    }}
                  />
                  <div className="iht-strategy-info">
                    <strong>Annual Gift Exemption</strong>
                    <span className="iht-strategy-ref">IHTA 1984 s.19</span>
                  </div>
                </div>
                <p className="iht-strategy-desc">
                  Give up to {'\u00A3'}3,000/year tax-free per person, with 1-year carry-forward of unused allowance.
                </p>
              </label>

              {/* Small Gifts */}
              <div className="iht-strategy-card">
                <div className="iht-strategy-toggle">
                  <input
                    type="checkbox"
                    checked={(watch('ihtStrategies.smallGiftRecipientsPerYear') ?? 0) > 0}
                    onChange={(e) => {
                      setValue('ihtStrategies.smallGiftRecipientsPerYear', e.target.checked ? 5 : 0, { shouldDirty: true });
                      handleBlur();
                    }}
                  />
                  <div className="iht-strategy-info">
                    <strong>Small Gifts</strong>
                    <span className="iht-strategy-ref">IHTA 1984 s.20</span>
                  </div>
                </div>
                <p className="iht-strategy-desc">
                  {'\u00A3'}250/recipient/year to any number of people (separate from annual exemption).
                </p>
                {(watch('ihtStrategies.smallGiftRecipientsPerYear') ?? 0) > 0 && (
                  <div className="iht-strategy-input-row">
                    <label htmlFor="small-gift-recipients" className="form-label">Recipients per year</label>
                    <input
                      id="small-gift-recipients"
                      type="number"
                      min="1"
                      max="50"
                      className={`form-input iht-strategy-number-input ${errors.ihtStrategies?.smallGiftRecipientsPerYear ? 'error' : ''}`}
                      {...register('ihtStrategies.smallGiftRecipientsPerYear', { valueAsNumber: true })}
                      onBlur={handleBlur}
                    />
                    {errors.ihtStrategies?.smallGiftRecipientsPerYear && (
                      <span className="error-message">Enter a number between 1 and 50</span>
                    )}
                  </div>
                )}
              </div>

              {/* Charitable Legacy */}
              <label className="iht-strategy-card">
                <div className="iht-strategy-toggle">
                  <input
                    type="checkbox"
                    {...register('ihtStrategies.charitableLegacyEnabled')}
                    onChange={(e) => {
                      void register('ihtStrategies.charitableLegacyEnabled').onChange(e);
                      handleBlur();
                    }}
                  />
                  <div className="iht-strategy-info">
                    <strong>Charitable Legacy</strong>
                    <span className="iht-strategy-ref">IHTA 1984 Sch.1A</span>
                  </div>
                </div>
                <p className="iht-strategy-desc">
                  Leave 10%+ of net estate to charity for a reduced 36% IHT rate (instead of 40%).
                </p>
              </label>

              {/* Normal Expenditure from Income */}
              <label className="iht-strategy-card">
                <div className="iht-strategy-toggle">
                  <input
                    type="checkbox"
                    {...register('ihtStrategies.normalExpenditureFromIncome')}
                    onChange={(e) => {
                      void register('ihtStrategies.normalExpenditureFromIncome').onChange(e);
                      handleBlur();
                    }}
                  />
                  <div className="iht-strategy-info">
                    <strong>Normal Expenditure from Income</strong>
                    <span className="iht-strategy-ref">IHTA 1984 s.21</span>
                  </div>
                </div>
                <p className="iht-strategy-desc">
                  Regular gifts from surplus income (income minus spending) are immediately exempt.
                </p>
              </label>

              {/* Potentially Exempt Transfers */}
              <div className="iht-strategy-card">
                <div className="iht-strategy-toggle">
                  <input
                    type="checkbox"
                    checked={(watch('ihtStrategies.annualPetAmount') ?? 0) > 0}
                    onChange={(e) => {
                      setValue('ihtStrategies.annualPetAmount', e.target.checked ? 10000 : 0, { shouldDirty: true });
                      handleBlur();
                    }}
                  />
                  <div className="iht-strategy-info">
                    <strong>Potentially Exempt Transfers (PETs)</strong>
                    <span className="iht-strategy-ref">IHTA 1984 s.3A</span>
                  </div>
                </div>
                <p className="iht-strategy-desc">
                  Larger gifts that become fully exempt after 7 years. Taper relief applies between 3-7 years.
                </p>
                {(watch('ihtStrategies.annualPetAmount') ?? 0) > 0 && (
                  <div className="iht-strategy-input-row">
                    <label htmlFor="annual-pet-amount" className="form-label">Annual PET amount ({'\u00A3'})</label>
                    <input
                      id="annual-pet-amount"
                      type="number"
                      min="1000"
                      step="1000"
                      className={`form-input iht-strategy-number-input ${errors.ihtStrategies?.annualPetAmount ? 'error' : ''}`}
                      {...register('ihtStrategies.annualPetAmount', { valueAsNumber: true })}
                      onBlur={handleBlur}
                    />
                    {errors.ihtStrategies?.annualPetAmount && (
                      <span className="error-message">Enter an amount of at least £1,000</span>
                    )}
                  </div>
                )}
              </div>

              {/* Charitable Donations */}
              <div className="iht-strategy-card">
                <div className="iht-strategy-toggle">
                  <input
                    type="checkbox"
                    checked={(watch('ihtStrategies.annualCharitableDonation') ?? 0) > 0}
                    onChange={(e) => {
                      setValue('ihtStrategies.annualCharitableDonation', e.target.checked ? 2000 : 0, { shouldDirty: true });
                      handleBlur();
                    }}
                  />
                  <div className="iht-strategy-info">
                    <strong>Charitable Donations</strong>
                    <span className="iht-strategy-ref">ITA 2007 s.413-430</span>
                  </div>
                </div>
                <p className="iht-strategy-desc">
                  Annual charitable giving with Gift Aid tax relief. Reduces estate and provides income tax benefit.
                </p>
                {(watch('ihtStrategies.annualCharitableDonation') ?? 0) > 0 && (
                  <div className="iht-strategy-input-row">
                    <label htmlFor="annual-charitable-donation" className="form-label">Annual donation ({'\u00A3'})</label>
                    <input
                      id="annual-charitable-donation"
                      type="number"
                      min="100"
                      step="100"
                      className={`form-input iht-strategy-number-input ${errors.ihtStrategies?.annualCharitableDonation ? 'error' : ''}`}
                      {...register('ihtStrategies.annualCharitableDonation', { valueAsNumber: true })}
                      onBlur={handleBlur}
                    />
                    {errors.ihtStrategies?.annualCharitableDonation && (
                      <span className="error-message">Enter an amount of at least £100</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </details>
        )}

        {/* Deceased Age Section */}
        <div className="twilight-section">
          <h3 className="twilight-section-header">Planning Horizon</h3>
          <p className="help-text">
            What age do you want to plan to? Costs, income, and care through this age are included
            in your projection. Leave blank to run the full projection period.
          </p>
          <p className="help-text assumptions-note">
            ONS 2022 data: UK life expectancy at age 65 is approximately 84 for men, 87 for women.
          </p>

          <div className="deceased-age-cards">
            {Array.from({ length: personCount }, (_, i) => (
              <div key={`person-${i}`} className="deceased-age-card">
                <label htmlFor={`deceased-age-${i}`} className="form-label">
                  {persons[i]?.fullName || `Person ${i + 1}`}
                </label>
                <div className="deceased-age-input-row">
                  <input
                    id={`deceased-age-${i}`}
                    type="number"
                    min="55"
                    max="120"
                    className={`form-input ${errors.personDeceasedAges?.[i]?.deceasedAge ? 'error' : ''}`}
                    placeholder="e.g. 90"
                    {...register(`personDeceasedAges.${i}.deceasedAge`, {
                      setValueAs: (v: string) => v === '' ? null : Number(v),
                    })}
                    onBlur={handleBlur}
                  />
                  <span className="deceased-age-unit">years old</span>
                </div>
                {errors.personDeceasedAges?.[i]?.deceasedAge && (
                  <span className="error-message">{errors.personDeceasedAges[i]?.deceasedAge?.message}</span>
                )}
                {(() => {
                  const deceasedAge = watch(`personDeceasedAges.${i}.deceasedAge`);
                  const dob = parseLocalDate(persons[i]?.dateOfBirth ?? '');
                  if (deceasedAge && dob) {
                    const calendarYear = dob.getFullYear() + deceasedAge;
                    return <span className="field-context-hint">Calendar year {calendarYear}</span>;
                  }
                  return null;
                })()}
              </div>
            ))}
          </div>

        </div>

        {/* Survivor Spending (joint plans only) */}
        {isJointPlan && (
          <div className="twilight-section">
            <h3 className="twilight-section-header">Survivor Spending</h3>
            <p className="help-text">
              After one person passes, household spending typically reduces.
              Set the percentage of normal spending the survivor will need.
            </p>
            <div className="survivor-spending-row">
              <div className="form-group">
                <label htmlFor="survivorSpendingPct" className="form-label">
                  Survivor Spending (% of normal)
                </label>
                <div className="survivor-spending-input-row">
                  <input
                    id="survivorSpendingPct"
                    type="number"
                    min="1"
                    max="100"
                    className={`form-input survivor-spending-input ${errors.survivorSpendingPct ? 'error' : ''}`}
                    {...register('survivorSpendingPct', { valueAsNumber: true })}
                    onBlur={handleBlur}
                  />
                  <span className="survivor-spending-unit">%</span>
                </div>
                {errors.survivorSpendingPct && (
                  <span className="error-message">{errors.survivorSpendingPct.message}</span>
                )}
                <span className="help-text">70% is a common rule of thumb for single-survivor households</span>
              </div>
            </div>
          </div>
        )}

        {/* Social Care Costs */}
        <div className="twilight-section care-section">
          <details open={careOpen} onToggle={(e) => setCareOpen((e.target as HTMLDetailsElement).open)}>
            <summary className="twilight-section-header care-summary">
              Social Care Costs (optional)
              {careFields.length > 0 && (
                <span className="care-count">{careFields.length}</span>
              )}
            </summary>
            {/* Ring-fenced care fund — sits WITH the care scenarios, because
                this is the first place the user has seen what care costs. */}
            {/* A switch only stops NEW input: a care fund the plan already holds stays editable. */}
            {(isFeatureEnabled('cashReserves') || (initialData?.careFund ?? 0) > 0) && (
            <div className="care-fund-row">
              <div className="form-group">
                <label htmlFor="careFund" className="form-label">
                  Set aside for later-life care (£)
                </label>
                <input
                  id="careFund"
                  type="text"
                  inputMode="numeric"
                  className={`form-input ${errors.careFund ? 'error' : ''}`}
                  placeholder="e.g. 50,000"
                  {...careFundInput}
                  onBlur={(e) => {
                    careFundInput.onBlur(e);
                    handleBlur();
                  }}
                />
                {errors.careFund && (
                  <span className="error-message">{errors.careFund.message}</span>
                )}
                <span className="help-text">
                  Kept in cash, in today&apos;s money, and released as care is paid for.
                  With no care scenario and no backstop age it stays ring-fenced for the whole plan.
                </span>
              </div>
              <div className="form-group">
                <label htmlFor="careFundReleaseAge" className="form-label">
                  Backstop age
                </label>
                <input
                  id="careFundReleaseAge"
                  type="number"
                  min="50"
                  max="120"
                  className={`form-input ${errors.careFundReleaseAge ? 'error' : ''}`}
                  placeholder="optional"
                  {...register('careFundReleaseAge', { valueAsNumber: true })}
                  onBlur={handleBlur}
                />
                {errors.careFundReleaseAge && (
                  <span className="error-message">{errors.careFundReleaseAge.message}</span>
                )}
                <span className="help-text">
                  Frees the fund once everyone still living has reached this age, whether or not care has started
                </span>
              </div>
            </div>
            )}

            <p className="help-text">
              Model potential care costs. UK care costs typically outpace general inflation by 1-2% per year.
              These are treated as essential spending and cannot be reduced by guardrails.
            </p>

            {careFields.map((field, index) => (
              <div key={field.id} className="care-card">
                <div className="care-card-header">
                  <span className="care-card-title">
                    {CARE_TYPE_PRESETS[watch(`careScenarios.${index}.careType`) || 'HOME']?.label || 'Care Scenario'}
                    {watch(`careScenarios.${index}.sellPrimaryResidenceOnCareStart`) && (
                      <span className="care-linked-badge">Includes home sale</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => { removeCare(index); setTimeout(handleBlur, 0); }}
                    className="btn-icon btn-delete"
                    aria-label="Delete care scenario"
                  >
                    ✕
                  </button>
                </div>

                <div className="care-card-fields">
                  {isJointPlan && (
                    <div className="form-group">
                      <label htmlFor={`care-person-${index}`} className="form-label">Person</label>
                      <select
                        id={`care-person-${index}`}
                        className="form-input"
                        {...register(`careScenarios.${index}.personIndex`, { valueAsNumber: true })}
                        onBlur={handleBlur}
                      >
                        {persons.map((p, pIdx: number) => (
                          <option key={`person-${pIdx}`} value={pIdx}>{p.fullName || `Person ${pIdx + 1}`}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-row two-columns">
                    <div className="form-group">
                      <label htmlFor={`care-type-${index}`} className="form-label">Care Type</label>
                      <select
                        id={`care-type-${index}`}
                        className="form-input"
                        value={watch(`careScenarios.${index}.careType`)}
                        onChange={(e) => handleCareTypeChange(index, e.target.value as CareScenarioItem['careType'])}
                        onBlur={handleBlur}
                      >
                        {(Object.entries(CARE_TYPE_PRESETS) as [CareScenarioItem['careType'], typeof CARE_TYPE_PRESETS['HOME']][]).map(([type, preset]) => (
                          <option key={type} value={type}>{preset.label} - {preset.description}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor={`care-cost-${index}`} className="form-label">Annual Cost (£/year)</label>
                      <input
                        id={`care-cost-${index}`}
                        type="number"
                        min="0"
                        className={`form-input ${errors.careScenarios?.[index]?.annualCost ? 'error' : ''}`}
                        {...register(`careScenarios.${index}.annualCost`, { valueAsNumber: true })}
                        onBlur={handleBlur}
                      />
                      {errors.careScenarios?.[index]?.annualCost && (
                        <span className="error-message">{errors.careScenarios[index]?.annualCost?.message}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-row two-columns">
                    <div className="form-group">
                      <label htmlFor={`care-start-${index}`} className="form-label">Starts (Year)</label>
                      <input
                        id={`care-start-${index}`}
                        type="number"
                        min="1"
                        max={initialData?.projectionYears || undefined}
                        className={`form-input ${errors.careScenarios?.[index]?.startYear ? 'error' : ''}`}
                        {...register(`careScenarios.${index}.startYear`, { valueAsNumber: true })}
                        onBlur={handleBlur}
                      />
                      {(() => {
                        const personIdx = watch(`careScenarios.${index}.personIndex`) ?? 0;
                        const startYear = watch(`careScenarios.${index}.startYear`);
                        const ageAtStart = ageAtPlanStartByPerson[personIdx];
                        if (startYear && ageAtStart != null && initialData?.planAsOfDate) {
                          const calendarYear = planYearToCalendar(startYear, initialData.planAsOfDate);
                          const age = ageAtStart + startYear - 1;
                          return <span className="field-context-hint">{formatYearAge({ planYear: startYear, calendarYear, people: [{ name: '', age }] }, 'inline')}</span>;
                        }
                        return null;
                      })()}
                      {initialData?.projectionYears && (
                        <span className="help-text">
                          Must be within projection period (year 1-{initialData.projectionYears}
                          {initialData?.planAsOfDate && ` / ${planYearToCalendar(1, initialData.planAsOfDate)}\u2013${planYearToCalendar(initialData.projectionYears, initialData.planAsOfDate)}`})
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor={`care-duration-${index}`} className="form-label">Duration (years)</label>
                      <input
                        id={`care-duration-${index}`}
                        type="number"
                        min="1"
                        max="30"
                        className={`form-input ${errors.careScenarios?.[index]?.durationYears ? 'error' : ''}`}
                        {...register(`careScenarios.${index}.durationYears`, { valueAsNumber: true })}
                        onBlur={handleBlur}
                        readOnly={watch(`careScenarios.${index}.untilEndOfLife`) === true}
                      />
                      {/* "Until end of life" checkbox */}
                      {(() => {
                        const personIdx = watch(`careScenarios.${index}.personIndex`) ?? 0;
                        const deceasedAge = watch(`personDeceasedAges.${personIdx}.deceasedAge`);
                        const ageAtStart = ageAtPlanStartByPerson[personIdx];
                        const label = deceasedAge != null && deceasedAge > 0 && ageAtStart != null
                          ? `Until end of life (through age ${deceasedAge})`
                          : 'Until end of projection';
                        return (
                          <label className="care-until-eol-label">
                            <input
                              type="checkbox"
                              checked={watch(`careScenarios.${index}.untilEndOfLife`) === true}
                              onChange={(e) => {
                                setValue(`careScenarios.${index}.untilEndOfLife`, e.target.checked, { shouldDirty: true });
                                if (e.target.checked) {
                                  const startYear = watch(`careScenarios.${index}.startYear`);
                                  const dur = computeEndOfLifeDuration(personIdx, startYear);
                                  if (dur != null) {
                                    setValue(`careScenarios.${index}.durationYears`, dur, { shouldValidate: true });
                                  }
                                }
                                handleBlur();
                              }}
                            />
                            <span className="care-until-eol-text">{label}</span>
                          </label>
                        );
                      })()}
                      {/* Duration hints: end age, gap warning, exceeds-life warning */}
                      {(() => {
                        const personIdx = watch(`careScenarios.${index}.personIndex`) ?? 0;
                        const startYear = watch(`careScenarios.${index}.startYear`);
                        const duration = watch(`careScenarios.${index}.durationYears`);
                        const careType = watch(`careScenarios.${index}.careType`) || 'HOME';
                        const ageAtStart = ageAtPlanStartByPerson[personIdx];
                        const deceasedAge = watch(`personDeceasedAges.${personIdx}.deceasedAge`);
                        const projYears = initialData?.projectionYears;

                        if (startYear && duration && ageAtStart != null && initialData?.planAsOfDate) {
                          const endCalendarYear = planYearToCalendar(startYear + duration - 1, initialData.planAsOfDate);
                          const endAge = ageAtStart + startYear - 1 + duration - 1;
                          const exceedsLife = deceasedAge != null && endAge > deceasedAge;
                          const exceedsProjection = projYears != null && (startYear + duration - 1) > projYears;
                          const gapToLife = deceasedAge != null ? deceasedAge - endAge : null;
                          const isOngoingType = careType === 'RESIDENTIAL' || careType === 'NURSING';
                          const hasGap = gapToLife != null && gapToLife > 0 && isOngoingType;

                          return (
                            <>
                              <span className="field-context-hint">
                                Ends {formatYearAge({ planYear: startYear + duration - 1, calendarYear: endCalendarYear, people: [{ name: '', age: endAge }] }, 'inline')}
                              </span>
                              {exceedsLife && (
                                <span className="field-context-hint exceeds-life">
                                  Duration extends beyond age {deceasedAge}. Costs after age {deceasedAge} will not be modelled.
                                </span>
                              )}
                              {exceedsProjection && !exceedsLife && (
                                <span className="field-context-hint exceeds-life">
                                  Care extends beyond projection period (year {projYears}). Costs will only be modelled to year {projYears}.
                                </span>
                              )}
                              {hasGap && (
                                <>
                                  <span className="field-context-hint care-gap-hint">
                                    Care ends {gapToLife} year{gapToLife > 1 ? 's' : ''} before life expectancy.
                                  </span>
                                  <button
                                    type="button"
                                    className="care-extend-btn"
                                    onClick={() => {
                                      const dur = computeEndOfLifeDuration(personIdx, startYear);
                                      if (dur != null) {
                                        setValue(`careScenarios.${index}.durationYears`, dur, { shouldValidate: true });
                                        setValue(`careScenarios.${index}.untilEndOfLife`, true, { shouldDirty: true });
                                        handleBlur();
                                      }
                                    }}
                                  >
                                    Extend to age {deceasedAge}
                                  </button>
                                </>
                              )}
                              <span className="field-context-hint">
                                Typical for {CARE_TYPE_PRESETS[careType].label.toLowerCase()}: {CARE_TYPE_PRESETS[careType].typicalDuration}
                              </span>
                            </>
                          );
                        }
                        return (
                          <span className="field-context-hint">
                            Typical for {CARE_TYPE_PRESETS[careType].label.toLowerCase()}: {CARE_TYPE_PRESETS[careType].typicalDuration}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor={`care-inflation-${index}`} className="form-label">Care Inflation Premium (% above CPI)</label>
                    <input
                      id={`care-inflation-${index}`}
                      type="number"
                      min="0"
                      max="10"
                      step="0.5"
                      className={`form-input care-inflation-input ${errors.careScenarios?.[index]?.inflationPremiumPct ? 'error' : ''}`}
                      {...register(`careScenarios.${index}.inflationPremiumPct`, { valueAsNumber: true })}
                      onBlur={handleBlur}
                    />
                    <span className="help-text">UK care costs typically outpace CPI by 1-2% per year</span>
                  </div>

                  {/* Sell primary residence on care entry — only for RESIDENTIAL/NURSING */}
                  {(watch(`careScenarios.${index}.careType`) === 'RESIDENTIAL' ||
                    watch(`careScenarios.${index}.careType`) === 'NURSING') && (
                    <div className="care-sell-residence-section">
                      {hasPrimaryResidence ? (
                        <>
                          <label className="form-label checkbox-label">
                            <input
                              type="checkbox"
                              {...register(`careScenarios.${index}.sellPrimaryResidenceOnCareStart`)}
                              onChange={(e) => {
                                void register(`careScenarios.${index}.sellPrimaryResidenceOnCareStart`).onChange(e);
                                setTimeout(handleBlur, 0);
                              }}
                            />
                            Sell {isDownsizing ? 'replacement property' : primaryResidenceLabel} when care begins
                          </label>
                          {isDownsizing && watch(`careScenarios.${index}.sellPrimaryResidenceOnCareStart`) && (
                            <p className="help-text assumptions-note">
                              Your plan includes a property downsizing (Step 4). The replacement property
                              will be sold at its inflation-adjusted value when care begins.
                            </p>
                          )}

                          {watch(`careScenarios.${index}.sellPrimaryResidenceOnCareStart`) && (
                            <>
                              <div className="form-group">
                                <label htmlFor={`care-proceeds-${index}`} className="form-label">What should happen to the sale proceeds?</label>
                                <select
                                  id={`care-proceeds-${index}`}
                                  className="form-input"
                                  {...register(`careScenarios.${index}.careSaleProceedsStrategy`)}
                                  onBlur={handleBlur}
                                >
                                  <option value="CASH">Hold as cash (to fund care)</option>
                                  <option value="INVEST">Invest in brokerage</option>
                                </select>
                              </div>
                              {isJointPlan && (
                                <p className="help-text assumptions-note">
                                  While your partner remains at home, the property is disregarded for means testing —
                                  the sale is suppressed until both partners have entered residential or nursing care.
                                </p>
                              )}
                              <details className="care-legal-details">
                                <summary className="help-text">Legal and tax assumptions — CGT relief, inheritance tax, and capacity</summary>
                                <p className="help-text assumptions-note">
                                  The property will be sold in the year care begins. No replacement property will be purchased.
                                  Full PPR relief is assumed (TCGA 1992 s.225) — if the property has not been your main residence
                                  throughout ownership, partial CGT may apply. Selling your home removes the £175,000 Residence
                                  Nil-Rate Band from your estate — the downsizing addition (IHTA 1984 s.8FA-8FE) may partially
                                  offset this. The addition is only available to the extent sufficient qualifying assets pass
                                  to direct descendants.
                                </p>
                                <p className="help-text assumptions-note care-disclosure-italic">
                                  This models a full outright sale. Alternatives — such as equity release or a council
                                  deferred payment agreement — are not modelled. This tool assumes the individual retains
                                  or has delegated financial decision-making capacity (e.g. via a Lasting Power of Attorney).
                                </p>
                              </details>
                            </>
                          )}
                        </>
                      ) : (
                        <p className="help-text assumptions-note">
                          No primary residence found. Add a property in Step 4 (Property), then return here to enable this option.
                          {onGoToStep && (
                            <>
                              {' '}
                              <button type="button" className="btn btn-link" onClick={() => onGoToStep(4)}>
                                Go to Step 4 →
                              </button>
                            </>
                          )}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <button type="button" onClick={addCareScenario} className="btn btn-secondary add-expense-btn">
              + Add Care Scenario
            </button>

            <div className="care-info-note">
              Note: Local authority means testing (£23,250 threshold) is not modelled.
              These costs represent self-funded care.
            </div>
          </details>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {onBack && (
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              Back
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => handleSubmit(onSubmit)()}>
            Next: Cash & Assumptions
          </button>
          {lastSaved && (
            <span className="save-indicator">
              Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default StepTwilightYears;
